import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { useSession } from '@/features/auth/session-provider';
import { ProfileAvatar } from '@/features/profiles/components/profile-avatar';
import {
  inviteCaregiver,
  listProfileAccess,
  revokeProfileAccess,
  type ProfileAccessWithProfile,
} from '@/features/profiles/lib/accessQueries';
import type { AccessLevel } from '@/types/db';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; rows: ProfileAccessWithProfile[] }
  | { kind: 'error'; message: string };

interface GroupedAccess {
  profileId: string;
  profileName: string;
  avatarColor: string | null;
  isOwner: boolean;
  rows: ProfileAccessWithProfile[];
}

const ACCESS_LABEL: Record<AccessLevel, string> = {
  owner: 'Owner',
  caregiver: 'Caregiver',
  view_only: 'View-only',
};

const ACCESS_HELPER: Record<Exclude<AccessLevel, 'owner'>, string> = {
  caregiver: 'Can add records and edit this timeline.',
  view_only: 'Can read this timeline but not edit.',
};

export function SettingsPage() {
  const { session, signOut } = useSession();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [toast, setToast] = useState<{
    message: string;
    kind: 'success' | 'error';
  } | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setState({ kind: 'loading' });
      const rows = await listProfileAccess();
      setState({ kind: 'ready', rows });
    } catch (err) {
      console.error('[settings] load access failed', err);
      setState({
        kind: 'error',
        message: "Couldn't load caregiver access.",
      });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(t);
  }, [toast]);

  const groups = useMemo<GroupedAccess[]>(() => {
    if (state.kind !== 'ready') return [];
    const myUserId = session?.user.id ?? null;
    const byProfile = new Map<string, GroupedAccess>();
    for (const row of state.rows) {
      let group = byProfile.get(row.profile.id);
      if (!group) {
        group = {
          profileId: row.profile.id,
          profileName: row.profile.name,
          avatarColor: row.profile.avatar_color,
          isOwner: false,
          rows: [],
        };
        byProfile.set(row.profile.id, group);
      }
      group.rows.push(row);
      if (
        row.user_id === myUserId &&
        row.access_level === 'owner'
      ) {
        group.isOwner = true;
      }
    }
    // Owner groups first, then view_only / caregiver groups; stable within.
    return Array.from(byProfile.values()).sort((a, b) => {
      if (a.isOwner === b.isOwner) {
        return a.profileName.localeCompare(b.profileName);
      }
      return a.isOwner ? -1 : 1;
    });
  }, [state, session]);

  async function handleRevoke(row: ProfileAccessWithProfile) {
    const myUserId = session?.user.id ?? null;
    if (row.user_id === myUserId && row.access_level === 'owner') return;
    const who =
      row.invited_email ??
      (row.user_id ? 'this caregiver' : 'this pending invite');
    const ok = window.confirm(
      `Remove ${who} from ${row.profile.name}'s timeline? This cannot be undone.`,
    );
    if (!ok) return;
    setRevoking(row.id);
    try {
      await revokeProfileAccess(row.id);
      setState((prev) => {
        if (prev.kind !== 'ready') return prev;
        return {
          kind: 'ready',
          rows: prev.rows.filter((r) => r.id !== row.id),
        };
      });
      setToast({ message: 'Access removed.', kind: 'success' });
    } catch (err) {
      console.error('[settings] revoke failed', err);
      const msg =
        err instanceof Error && err.message === 'revoke_no_match'
          ? "Couldn't remove — please refresh and try again."
          : "Couldn't remove access. Please try again.";
      setToast({ message: msg, kind: 'error' });
    } finally {
      setRevoking(null);
    }
  }

  return (
    <main className="min-h-screen safe-top safe-bottom">
      <header className="sticky top-0 z-10 border-b border-border bg-surface/95 backdrop-blur">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-2">
          <Link
            to="/home"
            aria-label="Back to family"
            className="h-10 w-10 -ml-1 rounded-full flex items-center justify-center text-ink-muted hover:text-ink active:bg-canvas shrink-0"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </Link>
          <h1 className="text-lg font-bold truncate flex-1">Settings</h1>
        </div>
      </header>

      <section className="px-4 py-6 max-w-md mx-auto space-y-6">
        <div className="card p-4">
          <p className="text-xs text-ink-muted mb-1">Signed in as</p>
          <p className="font-medium truncate">{session?.user.email}</p>
        </div>

        <div>
          <h2 className="text-base font-bold mb-3">Caregiver access</h2>

          {state.kind === 'loading' && (
            <div
              className="card p-6 h-32 animate-pulse bg-canvas"
              aria-busy="true"
            />
          )}

          {state.kind === 'error' && (
            <div className="card p-6 text-center">
              <p className="text-flag-block mb-3">{state.message}</p>
              <button type="button" onClick={load} className="btn-secondary">
                Try again
              </button>
            </div>
          )}

          {state.kind === 'ready' && groups.length === 0 && (
            <div className="card p-6 text-center">
              <p className="font-medium mb-1">No access yet</p>
              <p className="text-sm text-ink-muted">
                Add a family member first, then invite a caregiver from
                here.
              </p>
            </div>
          )}

          {state.kind === 'ready' &&
            groups.map((group) => (
              <AccessGroupCard
                key={group.profileId}
                group={group}
                myUserId={session?.user.id ?? null}
                myEmail={session?.user.email ?? null}
                revokingId={revoking}
                onRevoke={handleRevoke}
                onInvited={(row) => {
                  setState((prev) => {
                    if (prev.kind !== 'ready') return prev;
                    return { kind: 'ready', rows: [...prev.rows, row] };
                  });
                  setToast({ message: 'Invite added.', kind: 'success' });
                }}
              />
            ))}
        </div>

        <div className="rounded-card border border-flag-block/30 bg-red-50/50 p-4">
          <div className="flex items-center gap-2 mb-1">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-flag-block" aria-hidden="true">
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            </svg>
            <h2 className="text-base font-bold text-flag-block">Danger zone</h2>
          </div>
          <p className="text-sm text-ink-muted mb-3">
            Signing out clears this device. Your records stay safe in the cloud.
          </p>
          <button
            type="button"
            onClick={signOut}
            className="btn w-full bg-surface text-flag-block border border-flag-block/40 hover:bg-red-50"
          >
            Sign out
          </button>
        </div>
      </section>

      {toast && (
        <div
          role={toast.kind === 'error' ? 'alert' : 'status'}
          className={clsx(
            'fixed top-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full shadow-pop text-sm z-50',
            toast.kind === 'error'
              ? 'bg-flag-block text-white'
              : 'bg-accent text-white',
          )}
        >
          {toast.message}
        </div>
      )}
    </main>
  );
}

interface AccessGroupCardProps {
  group: GroupedAccess;
  myUserId: string | null;
  myEmail: string | null;
  revokingId: string | null;
  onRevoke: (row: ProfileAccessWithProfile) => void;
  onInvited: (row: ProfileAccessWithProfile) => void;
}

function AccessGroupCard({
  group,
  myUserId,
  myEmail,
  revokingId,
  onRevoke,
  onInvited,
}: AccessGroupCardProps) {
  return (
    <div className="card p-4 mb-3">
      <div className="flex items-center gap-3 mb-3">
        <ProfileAvatar name={group.profileName} color={group.avatarColor} />
        <div className="min-w-0 flex-1">
          <p className="font-bold truncate">{group.profileName}</p>
          <p className="text-xs text-ink-muted">
            {group.isOwner ? 'You own this timeline' : 'You have access'}
          </p>
        </div>
      </div>

      <ul className="divide-y divide-border -mx-4">
        {group.rows.map((row) => (
          <AccessRow
            key={row.id}
            row={row}
            isGroupOwner={group.isOwner}
            myUserId={myUserId}
            myEmail={myEmail}
            revoking={revokingId === row.id}
            onRevoke={onRevoke}
          />
        ))}
      </ul>

      {group.isOwner && (
        <InviteRow
          profileId={group.profileId}
          profileName={group.profileName}
          existingRows={group.rows}
          onInvited={(row) =>
            onInvited({
              ...row,
              profile: {
                id: group.profileId,
                name: group.profileName,
                avatar_color: group.avatarColor,
                relation: null,
              },
            })
          }
        />
      )}
    </div>
  );
}

interface AccessRowProps {
  row: ProfileAccessWithProfile;
  isGroupOwner: boolean;
  myUserId: string | null;
  myEmail: string | null;
  revoking: boolean;
  onRevoke: (row: ProfileAccessWithProfile) => void;
}

function AccessRow({
  row,
  isGroupOwner,
  myUserId,
  myEmail,
  revoking,
  onRevoke,
}: AccessRowProps) {
  const isSelf = row.user_id === myUserId;
  const isSelfOwner = isSelf && row.access_level === 'owner';
  const isPending = !row.accepted_at;
  const who = isSelf
    ? myEmail
      ? `${myEmail} (you)`
      : isSelfOwner
        ? 'You (owner)'
        : 'You'
    : (row.invited_email ?? 'Caregiver');
  // Owners of the group may revoke anyone except themselves.
  // Non-owners in the group can revoke their OWN row (leave the share).
  const canRevoke = isGroupOwner ? !isSelfOwner : isSelf && !isSelfOwner;

  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="text-sm font-medium truncate">{who}</p>
          {isPending && (
            <span className="inline-flex items-center gap-1 shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-flag-warn border border-flag-warn/30">
              <span
                aria-hidden="true"
                className="h-1.5 w-1.5 rounded-full bg-flag-warn"
              />
              Pending
            </span>
          )}
        </div>
        <p className="text-xs text-ink-muted">
          {ACCESS_LABEL[row.access_level]}
        </p>
      </div>
      {canRevoke ? (
        <button
          type="button"
          onClick={() => onRevoke(row)}
          disabled={revoking}
          className="text-sm text-flag-block hover:underline disabled:opacity-50 min-h-tap px-2"
        >
          {revoking ? 'Removing…' : 'Remove'}
        </button>
      ) : (
        <span className="text-xs text-ink-subtle">—</span>
      )}
    </li>
  );
}

interface InviteRowProps {
  profileId: string;
  profileName: string;
  existingRows: ProfileAccessWithProfile[];
  onInvited: (row: ProfileAccessWithProfile) => void;
}

function InviteRow({
  profileId,
  profileName,
  existingRows,
  onInvited,
}: InviteRowProps) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [accessLevel, setAccessLevel] =
    useState<Exclude<AccessLevel, 'owner'>>('caregiver');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError('Enter an email to invite.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("That doesn't look like a valid email.");
      return;
    }
    if (existingRows.some((r) => r.invited_email === trimmed)) {
      setError(`${trimmed} already has access to ${profileName}.`);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const row = await inviteCaregiver({
        profileId,
        email: trimmed,
        accessLevel,
      });
      onInvited({
        ...row,
        profile: {
          id: profileId,
          name: profileName,
          avatar_color: null,
          relation: null,
        },
      });
      setEmail('');
      setOpen(false);
    } catch (err) {
      console.error('[settings] invite failed', err);
      const msg =
        err instanceof Error && err.message === 'duplicate_invite'
          ? `${trimmed} is already invited.`
          : "Couldn't add the invite. Please try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return (
      <div className="mt-3 pt-3 border-t border-border">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="min-h-tap w-full inline-flex items-center justify-center gap-1.5 rounded-card border border-dashed border-border text-sm font-medium text-ink-muted hover:text-accent hover:border-accent active:bg-accent-soft/40 transition-colors py-2.5"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
            aria-hidden="true"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Invite caregiver
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-3 pt-3 border-t border-border">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-ink-muted">Invite a caregiver</p>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
          className="text-xs text-ink-muted hover:text-ink min-h-tap -m-2 p-2"
        >
          Cancel
        </button>
      </div>
      <div className="flex flex-col gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="input"
          placeholder="caregiver@example.com"
          autoComplete="off"
          maxLength={254}
          autoFocus
        />
        <select
          value={accessLevel}
          onChange={(e) =>
            setAccessLevel(e.target.value as Exclude<AccessLevel, 'owner'>)
          }
          className="input"
        >
          <option value="caregiver">Caregiver — can add and edit</option>
          <option value="view_only">View-only — read only</option>
        </select>
        <p className="text-xs text-ink-subtle">
          {ACCESS_HELPER[accessLevel]} They'll see {profileName}'s timeline
          when they sign up with this email.
        </p>
        {error && (
          <p className="text-sm text-flag-block" role="alert">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="btn-secondary w-full"
        >
          {submitting ? 'Adding…' : 'Add invite'}
        </button>
      </div>
    </form>
  );
}
